import React from "react";
import type { LeaveRequest, LeaveType } from "../../../api/leave/Leave.typed";
import { Box, Table, Chip, IconButton, Tooltip } from "@mui/joy";
import DarkTypography from "../../common/DarkTypography";
import IconButtonCTA from "../../common/IconButtonCTA";
import MotifCell from "./MotifCell";
import { formatLeaveDurationLabel, formatLeavePeriod } from "../../../utils/leaveHelpers";
import useAppSettings from "../../../contexts/app/appSettings";
import type { LeaveMotifVisibilityOpts } from "../../../utils/planningLeaveHelpers";
import { shouldDisplayLeaveMotifForUi } from "../../../utils/planningLeaveHelpers";
import { formatDate } from "../../../utils/dateTime";
import { SComment, SInfo, CancelIcon } from "../../../styles/StyledMUI";
import {
  ctaAccentColor,
  hrCompactTableSx,
  hrCommentCountChipSx,
  hrTooltipSurfaceSx,
  textTokens,
} from "../../../styles/globalStyles";

interface LeaveRequestListDesktopProps {
  requests: LeaveRequest[];
  typesMap: Record<number, LeaveType>;
  canCancelOrDeleteLocal: (request: LeaveRequest) => boolean;
  handleCancelOrDelete: (request: LeaveRequest) => void;
  setOpenCommentsRequestId: (requestId: number) => void;
  renderStatusChip: (status: number) => React.ReactNode;
  leaveMotifVisibilityOpts?: LeaveMotifVisibilityOpts;
}

export default function LeaveRequestListDesktop(
  props: LeaveRequestListDesktopProps,
): React.ReactNode {
  const { config } = useAppSettings();
  const companyTimeZone = config?.TimeZone;

  return (
    <Box sx={{ display: { xs: "none", md: "block" } }}>
      <Table hoverRow size="sm" sx={hrCompactTableSx}>
        <thead>
          <tr>
            <th style={{ width: 40 }}></th>
            <th style={{ width: 160 }}>Type</th>
            <th style={{ width: 270 }}>Période</th>
            <th style={{ width: 90 }}>Durée</th>
            <th style={{ width: 140 }}>Statut</th>
            <th>Motif</th>
            <th style={{ width: 130 }}>Commentaires</th>
            <th style={{ width: 80 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {Array.isArray(props.requests)
            ? props.requests.map((request) => (
                <tr key={request.id}>
                  <td>
                    <Tooltip
                      sx={hrTooltipSurfaceSx}
                      title={
                        <Box component="span" sx={{ display: "block" }}>
                          <DarkTypography level="body-sm">
                            Demande : {formatDate(request.created_at)}
                          </DarkTypography>
                          <DarkTypography level="body-sm">
                            Mise à jour : {formatDate(request.updated_at)}
                          </DarkTypography>
                        </Box>
                      }
                    >
                      <IconButton size="sm" variant="plain" sx={{ color: textTokens.color.dimmed }}>
                        <SInfo />
                      </IconButton>
                    </Tooltip>
                  </td>
                  <td>
                    <DarkTypography level="body-sm">
                      {props.typesMap[request.type_id]?.Name || "—"}
                    </DarkTypography>
                  </td>
                  <td>
                    <DarkTypography level="body-sm">
                      {formatLeavePeriod(request, companyTimeZone)}
                    </DarkTypography>
                  </td>
                  <td>
                    <DarkTypography level="body-sm" sx={{ fontWeight: "bold" }}>
                      {formatLeaveDurationLabel(request, companyTimeZone)}
                    </DarkTypography>
                  </td>
                  <td>{props.renderStatusChip(request.status)}</td>
                  <td>
                    {(() => {
                      const motifVisible = shouldDisplayLeaveMotifForUi(
                        request,
                        Object.values(props.typesMap),
                        props.leaveMotifVisibilityOpts,
                      );

                      return (
                        <MotifCell
                          text={motifVisible ? request.reason || "—" : "—"}
                          hasReason={motifVisible && !!request.reason}
                          maxWidth={260}
                        />
                      );
                    })()}
                  </td>
                  <td>
                    <Box
                      sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}
                      onClick={() => props.setOpenCommentsRequestId(request.id)}
                    >
                      <SComment iconColor={ctaAccentColor} />
                      <Chip size="sm" variant="soft" color="neutral" sx={hrCommentCountChipSx}>
                        {Array.isArray(request.multi_comments) ? request.multi_comments.length : 0}
                      </Chip>
                    </Box>
                  </td>
                  <td>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      {props.canCancelOrDeleteLocal(request) && (
                        <IconButtonCTA
                          aria-label="Annuler ou supprimer la demande"
                          ctaVariant="danger"
                          size="sm"
                          onClick={() => props.handleCancelOrDelete(request)}
                        >
                          <CancelIcon />
                        </IconButtonCTA>
                      )}
                    </Box>
                  </td>
                </tr>
              ))
            : null}
        </tbody>
      </Table>
    </Box>
  );
}
